/**
 * Provide different methods for Creator to upload their media
 *   1. Upload Film to S3
 *   2. Link to Video file
 * Else
 *   3. Write to submit@viddsee.com automatically.
 *      (trigger email if system did not detect any uploads or given link to video)
 *
 * Note:
 *   User will be able to upload both video file and video link in this component.
 *   For display purposes, video file will have a higher priority over link submitted.
 *   i.e. The upload status slot will only show one type of submission at a time.
 *        If both types are submitted. Only the video filename will be shown inside the upload status.
 *   The urlDropper (as a side effect. It is not implemented to accomondate the above restriction) will
 *   always show the submitted link regardless of the above restriction.
 */


<template>
  <div class="video-upload">
    <h2 class="is-required--custom">
      Video Upload
    </h2>

    <div style="position: relative;">
      <VideoUploader
        ref="videoUploader"
        class="video-uploader"
        :submissionId="value.id"
        v-model="formData.video_file_url"
        @submit="updateForm"
        :backgroundUploadFn="directUpload"
        :disabled="!editable"
      >
        <!-- Show progress, action and information about the file -->
        <div
          class="video-uploader__upload-info upload-info"
          @mouseenter="showAction"
          @mouseleave="hideAction"
        >
          <!-- Feedback -->
          <div class="upload-info__feedback">
            <ProgressIndicator
              :progress="uploadProgress"
              :status="uploadState"
            />
          </div>

          <!-- Actions -->
          <div class="upload-info__action">
            <div v-if="uploadState === 'nothing'">
              <el-button v-if="editable"
                ref="actionButton"
                @click="addUpload"
              >Choose a video file
              </el-button>
              <span v-else>
                There is no upload for this submission
              </span>
            </div>

            <el-button v-else
              @click="removeUpload"
            >
              <span v-if="actionVisibility">
                <span v-if="uploadState === 'uploading'"><i class="el-icon-close"></i> Cancel</span>
                <span v-else-if="uploadState === 'uploaded'"><i class="el-icon-delete"></i> Remove</span>
              </span>

              <span v-else>
                <span v-if="uploadState === 'uploading'"><i class="el-icon-loading"></i> Uploading</span>
                <span v-else-if="uploadState === 'uploaded'"><i class="el-icon-circle-check-outline"></i> {{ uploadStatusText }}</span>
              </span>
            </el-button>
          </div>

          <!-- File information -->
          <div class="upload-info__file">
            <p v-if="uploadState === 'nothing' && editable">
              or, drag a video file here
            </p>
            <p v-else-if="uploadedMeta"
              class="el-upload-list__item"
            >
              <span v-if="uploadedMeta.type === 'file'">
                {{ uploadedMeta.text }}
              </span>
              <a v-if="uploadedMeta.type === 'link'"
                :href="uploadedMeta.text"
                target="_blank"
                class="el-button el-button--text"
              >
                {{ uploadedMeta.text }}
              </a>
            </p>
          </div>
        </div>

      </VideoUploader>
    </div>
    <div style="text-align: right;">
      <el-button
        size="mini"
        plain
        style="border: none; opacity: 0.75;"
        @click="openVideoFormatRecommendation"
      >
        <i class="el-icon-info"/> Click here to see the recommended video format
      </el-button>
    </div>
    <div style="margin-bottom: 1em;"></div>

    <UrlDropper
      ref="urlDropper"
      :value="formData"
      @input="updateUrl"
      :disabled="!editable"
    ></UrlDropper>
  </div>
</template>

<script>
import NOTIFY_CONSTANTS from '@/config/notify-constants';
import VideoUploader from './VideoUploader';
import UrlDropper from './UrlDropper';
import ProgressIndicator from './ProgressIndicator';
import stepMixin from '../../Mixins/stepMixin';

export default {
  name: 'upload',
  mixins: [
    stepMixin,
  ],
  components: {
    VideoUploader,
    UrlDropper,
    ProgressIndicator,
  },
  props: {
    value: {},
    uploadQueue: {},
  },
  computed: {
    uploadState() {
      if (this.uploadQueue) {
        return 'uploading';
      } else if (this.hasUploaded) {
        return 'uploaded';
      } else {
        return 'nothing';
      }
    },
    hasUploaded() {
      return !!this.value.video_file_url || !!this.value.video_file_url_alt;
    },
    /**
     * Compute a name for the uploaded data.
     * If a file is uploaded, it should return the file's original filename.
     * Otherwise, if a link is submitted, it should return the full url of the link.
     * Else return null.
     */
    uploadedMeta() {
      if (this.uploadQueue && this.uploadQueue.fileMeta) {
        return {
          text: this.uploadQueue.fileMeta.file.name,
          type: 'file',
        };
      } else if (this.value.video_file_url) {
        return {
          text: (this.value.multiupload_info) ? JSON.parse(this.value.multiupload_info).filename : '',
          type: 'file',
        };
      } else if (this.value.video_file_url_alt) {
        return {
          text: this.value.video_file_url_alt,
          type: 'link',
        };
      } else {
        return null;
      }
    },
    uploadStatusText() {
      if (this.value.video_file_url) return 'Uploaded';
      else if (this.value.video_file_url_alt) return 'URL provided';
      else return '';
    },
    uploadProgress() {
      if (this.uploadQueue) {
        return this.uploadQueue.progress || 0;
      } else if (this.hasUploaded) {
        return 1;
      } else {
        return 0;
      }
    },
  },
  data() {
    return {
      formData: {
        video_file_url: this.value.video_file_url,
        video_file_url_alt: this.value.video_file_url_alt,
        alt_file_credentials: this.value.alt_file_credentials,
      },
      actionVisibility: false,
    };
  },
  methods: {
    // UI Interaction
    showAction() { this.actionVisibility = true; },
    hideAction() { this.actionVisibility = false; },
    openVideoFormatRecommendation() {
      const recommendation = 'https://s3-ap-southeast-1.amazonaws.com/viddsee/documents/Video+Requirements.pdf';
      const win = window.open(recommendation, '_blank');
      win.focus();
    },
    // Parent communication methods
    updateVideo({ url }) {
      this.formData.video_file_url = url;
      this.updateForm();
    },
    updateUrl({ url, password }) {
      if (url != null) {
        this.formData.video_file_url_alt = url;
      }
      if (password != null) {
        this.formData.alt_file_credentials = password;
      }
      this.updateForm();
    },
    updateForm() {
      this.$emit('input', this.formData);
    },
    directUpload(url, fileMeta) {
      // TODO: Review implementation.
      //       This deviated from current architecture
      // TODO: DRY this section. This is copied from Wrapper.vue
      this.$store.commit('networkActivity/loading');

      this.$store.dispatch('submission/update', {
        id: this.value.id,
        video_file_url: url,
        multiupload_info: JSON.stringify({
          // TODO: multiupload_info seems to be a legacy field. Perhaps, it should be deprecated and replaced with fileMeta
          filename: fileMeta.file.name,
        }),
      })
        .then(() => {
          this.$notify.success(`The video "${fileMeta.file.name}" has been uploaded successfully.`);
          this.$store.commit('networkActivity/success');
        })
        .catch((error) => {
          this.$store.commit('networkActivity/error');
          this.$notify.error({
            title: error.message,
            message: 'Error uploading video file.',
            duration: NOTIFY_CONSTANTS.DURATION.FOREVER,
          });
          throw error;
        });
    },
    generateWarningMessage(filename, type) {
      const h = this.$createElement;
      return h('p', null, [
        h('span', null, `This will remove the submitted video ${type}: `),
        h('i', null, filename),
      ]);
    },
    generateWarningBox(method) {
      return this.$confirm(null, 'Are you sure?', {
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        message: this.generateWarningMessage(this.uploadedMeta.text, this.uploadedMeta.type),
      })
        .then(() => method())
        .catch(() => {});
    },

    // Upload actions
    addUpload() {
      this.$refs.videoUploader.openSystemBrowseDialogue();
      // Immediately unfocus the button after the it is clicked.
      this.$refs.actionButton.$el.blur();
    },
    removeUpload() {
      if (this.uploadQueue) {
        this.$store.dispatch('uploadQueue/dequeueVideo', this.value.id);
        this.$notify.info('Upload is cancelled');
      } else if (this.value.video_file_url) {
        this.generateWarningBox(() => {
          this.updateVideo({
            url: '',
          });
        });
      } else if (this.value.video_file_url_alt) {
        this.generateWarningBox(() => {
          this.updateUrl({
            url: '',
            password: '',
          });
          this.$refs.urlDropper.clear();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.video-upload {
  width: 100%;
  display: block;
  margin: auto;
}

.upload-status-action {
  border-color: transparent;
  margin-bottom: 0.5em;
  transition: border-color 0.3s;
}
.upload-status-action--remove {
  border-color: #d8dce5;
  margin-right: 0;
  transition: border-color 0.3s;

  &:hover {
    border-color: #f2b9ce;
  }
}
</style>

<style lang="scss" scoped>

.upload-info__feedback {}
.upload-info__action {}
.upload-info__file {
  max-width: 320px;
  word-break: break-all;
}
</style>



// WEBPACK FOOTER //
// src/components/Submission/steps/VideoUpload/index.vue